import {
  generateMonthYearInRange,
  getMonthName,
  MonthYearPeriod,
  PeriodName as CommonPeriodName,
  getMonthLongAbbreviation,
  dateToMonthYear,
  MonthYear,
  createError,
  uuidSchema,
  toMonthYearPeriod,
} from '@common/neo.tax';
import sortBy from 'lodash/sortBy';
import z from 'zod';

import { PeriodName as GqlPeriodName, PeriodName, ProductType } from '../graphql';
import { FiscalYear } from '../hooks/useAllFiscalYears';
import { ProductTypeSchema } from './product.utils';

type YtdPeriodName =
  | GqlPeriodName.Ytd1
  | GqlPeriodName.Ytd2
  | GqlPeriodName.Ytd3
  | GqlPeriodName.Ytd4
  | GqlPeriodName.Ytd5
  | GqlPeriodName.Ytd6
  | GqlPeriodName.Ytd7
  | GqlPeriodName.Ytd8
  | GqlPeriodName.Ytd9
  | GqlPeriodName.Ytd10
  | GqlPeriodName.Ytd11
  | GqlPeriodName.Ytd12;
type MonthPeriodName =
  | GqlPeriodName.M1
  | GqlPeriodName.M2
  | GqlPeriodName.M3
  | GqlPeriodName.M4
  | GqlPeriodName.M5
  | GqlPeriodName.M6
  | GqlPeriodName.M7
  | GqlPeriodName.M8
  | GqlPeriodName.M9
  | GqlPeriodName.M10
  | GqlPeriodName.M11
  | GqlPeriodName.M12;

const gqlPeriodNameSchema = z.enum(Object.values(GqlPeriodName));

export const safeParseProduct = (
  product: string,
): { product: ProductType; success: true } | { product: null; success: false } => {
  const productParseResult = ProductTypeSchema.safeParse(product);
  if (!productParseResult.success) {
    return {
      success: false,
      product: null,
    };
  }
  return {
    success: true,
    product: productParseResult.data,
  };
};

export const periodDisplayName = (period: MonthYearPeriod, duplicateMonthYear = false): string => {
  const monthYears = generateMonthYearInRange(period);
  if (monthYears.length === 1 && !duplicateMonthYear) {
    return `${getMonthName(period.startMonthYear.month)} ${period.startMonthYear.year}`;
  }
  return `${getMonthName(period.startMonthYear.month)} ${period.startMonthYear.year} - ${getMonthName(
    period.endMonthYear.month,
  )} ${period.endMonthYear.year}`;
};

const getFiscalYearIdStorageKey = (companyId: string, selectedProduct: ProductType): string =>
  `FISCAL_YEAR_ID_STORAGE_${companyId}_${selectedProduct}`;

const getPeriodNameStorageKey = (companyId: string, selectedProduct: ProductType): string =>
  `PERIOD_NAME_STORAGE_${companyId}_${selectedProduct}`;

const getProductStorageKey = (companyId: string): string => `PRODUCT_STORAGE_${companyId}`;

export const getProductFromLocalStorage = (companyId: string): ProductType | null => {
  const product = localStorage.getItem(getProductStorageKey(companyId));
  if (!product) {
    return null;
  }

  const productParseResult = safeParseProduct(product);
  if (!productParseResult.success) {
    return null;
  }
  return productParseResult.product;
};

export const getFiscalYearIdFromLocalStorage = (companyId: string, selectedProduct: ProductType): string | null => {
  const fiscalYearId = localStorage.getItem(getFiscalYearIdStorageKey(companyId, selectedProduct));
  if (!fiscalYearId) {
    return null;
  }

  const fiscalYearIdParseResult = uuidSchema.safeParse(fiscalYearId);
  if (!fiscalYearIdParseResult.success) {
    return null;
  }
  return fiscalYearIdParseResult.data;
};

export const getPeriodNameFromLocalStorage = (companyId: string, selectedProduct: ProductType): PeriodName | null => {
  const periodName = localStorage.getItem(getPeriodNameStorageKey(companyId, selectedProduct));
  if (!periodName) {
    return null;
  }
  const periodNameParseResult = gqlPeriodNameSchema.safeParse(periodName);
  if (!periodNameParseResult.success) {
    return null;
  }
  return periodNameParseResult.data;
};

export const removeProductSelectionFromLocalStorage = (companyId: string): void => {
  localStorage.removeItem(getProductStorageKey(companyId));
  localStorage.removeItem(getFiscalYearIdStorageKey(companyId, ProductType.UsRd));
  localStorage.removeItem(getFiscalYearIdStorageKey(companyId, ProductType.UsAsc));
  localStorage.removeItem(getPeriodNameStorageKey(companyId, ProductType.UsRd));
  localStorage.removeItem(getPeriodNameStorageKey(companyId, ProductType.UsAsc));
};

export const storeProductAndPeriodInLocalStorage = (
  companyId: string,
  fiscalYearId: string,
  periodName: PeriodName,
  selectedProduct: ProductType,
): void => {
  localStorage.setItem(getProductStorageKey(companyId), selectedProduct);
  localStorage.setItem(getFiscalYearIdStorageKey(companyId, selectedProduct), fiscalYearId);
  localStorage.setItem(getPeriodNameStorageKey(companyId, selectedProduct), periodName);
};

const isMonthYearInFuture = (monthYear: MonthYear): boolean => {
  const today = new Date();
  const firstOfMonth = new Date(monthYear.year, monthYear.month, 1);
  return today < firstOfMonth;
};

export type PeriodNameOption = {
  label: string;
  value: string;
  disabled: boolean;
  periodName: GqlPeriodName;
};

export type PeriodNameOptionGroup = {
  label?: string;
  options: PeriodNameOption[];
};

// TODO: Add tests
const getQuarterPeriodName = (quarter: number): GqlPeriodName => {
  switch (quarter) {
    case 1:
      return GqlPeriodName.Q1;
    case 2:
      return GqlPeriodName.Q2;
    case 3:
      return GqlPeriodName.Q3;
    case 4:
      return GqlPeriodName.Q4;
    default:
      throw createError('InvalidQuarterError', 'Invalid quarter');
  }
};

const getMonthPeriodName = (month: number): MonthPeriodName => {
  switch (month) {
    case 0:
      return GqlPeriodName.M1;
    case 1:
      return GqlPeriodName.M2;
    case 2:
      return GqlPeriodName.M3;
    case 3:
      return GqlPeriodName.M4;
    case 4:
      return GqlPeriodName.M5;
    case 5:
      return GqlPeriodName.M6;
    case 6:
      return GqlPeriodName.M7;
    case 7:
      return GqlPeriodName.M8;
    case 8:
      return GqlPeriodName.M9;
    case 9:
      return GqlPeriodName.M10;
    case 10:
      return GqlPeriodName.M11;
    case 11:
      return GqlPeriodName.M12;
    default:
      throw createError('InvalidMonthError', 'Invalid month');
  }
};

const monthToYtd = (month: MonthPeriodName): YtdPeriodName => {
  switch (month) {
    case GqlPeriodName.M1:
      return GqlPeriodName.Ytd1;
    case GqlPeriodName.M2:
      return GqlPeriodName.Ytd2;
    case GqlPeriodName.M3:
      return GqlPeriodName.Ytd3;
    case GqlPeriodName.M4:
      return GqlPeriodName.Ytd4;
    case GqlPeriodName.M5:
      return GqlPeriodName.Ytd5;
    case GqlPeriodName.M6:
      return GqlPeriodName.Ytd6;
    case GqlPeriodName.M7:
      return GqlPeriodName.Ytd7;
    case GqlPeriodName.M8:
      return GqlPeriodName.Ytd8;
    case GqlPeriodName.M9:
      return GqlPeriodName.Ytd9;
    case GqlPeriodName.M10:
      return GqlPeriodName.Ytd10;
    case GqlPeriodName.M11:
      return GqlPeriodName.Ytd11;
    case GqlPeriodName.M12:
      return GqlPeriodName.Ytd12;
    default:
      throw createError('InvalidMonthError', 'Invalid month');
  }
};

/**
 * Generates quarter period options based on fiscal year end date
 */
const generateQuarterOptions = (endMonth: number, endYear: number): PeriodNameOption[] => {
  const quarters = [0, 1, 2, 3].map((i) => {
    const periodEndMonth = (endMonth - 3 * i + 12) % 12;
    let periodEndYear = endYear;
    if (periodEndMonth > endMonth) {
      periodEndYear -= 1;
    }
    const periodLabel = `Q${4 - i}`;

    return {
      label: periodLabel,
      value: periodLabel,
      periodName: getQuarterPeriodName(4 - i),
      // Disable the period if it hasn't started yet (month/year is in the future)
      disabled: isMonthYearInFuture({ month: periodEndMonth, year: periodEndYear }),
    };
  });

  return sortBy(quarters, (quarter) => quarter.label);
};

/**
 * Generates monthly period options based on fiscal year end date
 */
const generateMonthOptions = (
  endMonth: number,
  endYear: number,
): {
  label: string;
  value: string;
  disabled: boolean;
  periodName: MonthPeriodName;
}[] => {
  const startMonth = (endMonth + 1) % 12; // First month of fiscal year

  const months = Array.from({ length: 12 }, (_, i) => {
    const periodMonth = (endMonth - i + 12) % 12;
    let periodYear = endYear;
    if (periodMonth > endMonth) {
      periodYear -= 1;
    }

    const periodLabel =
      endMonth === 11
        ? `${getMonthLongAbbreviation(periodMonth)}`
        : `${getMonthLongAbbreviation(periodMonth)}, ${periodYear}`;

    // Calculate fiscal month index (0-indexed position within fiscal year)
    // This maps calendar months to their position in the fiscal year
    const fiscalMonthIndex = (periodMonth - startMonth + 12) % 12;
    const periodName = getMonthPeriodName(fiscalMonthIndex);

    return {
      label: periodLabel,
      value: periodLabel,
      periodName,
      monthNum: periodMonth,
      disabled: isMonthYearInFuture({ month: periodMonth, year: periodYear }),
    };
  });

  return sortBy(months, (month) => {
    const monthNum = month.monthNum;
    // Calculate relative position from fiscal year start
    // E.g. if fiscal year ends in August (8), September (9) should be first
    const relativePosition = (monthNum - startMonth + 12) % 12;
    return relativePosition;
  }).map((month) => ({
    label: month.label,
    value: month.value,
    periodName: month.periodName,
    disabled: month.disabled,
  }));
};

/**
 * Generates half-year period options based on quarter options
 */
const generateHalfOptions = (sortedQuarters: PeriodNameOption[]): PeriodNameOption[] => [
  {
    label: 'H1',
    value: 'H1',
    periodName: GqlPeriodName.H1,
    disabled: sortedQuarters[1].disabled,
  },
  {
    label: 'H2',
    value: 'H2',
    periodName: GqlPeriodName.H2,
    disabled: sortedQuarters[3].disabled,
  },
];

/**
 * Generates the default ASC period option based on ASC settings and completion status
 */
const generateDefaultAscPeriodOption = (
  sortedMonths: { label: string; value: string; periodName: MonthPeriodName; disabled: boolean }[],
): PeriodNameOption | null => {
  const lastCompletedMonth = [...sortedMonths].reverse().find((m) => !m.disabled);
  if (!lastCompletedMonth) {
    return null;
  }

  const ytdPeriodName = monthToYtd(lastCompletedMonth.periodName);
  const defaultPeriodName = ytdPeriodName === GqlPeriodName.Ytd12 ? GqlPeriodName.A : ytdPeriodName;
  const defaultPeriodValue = defaultPeriodName === GqlPeriodName.A ? 'Annual' : 'YTD';

  return {
    label: defaultPeriodValue,
    value: defaultPeriodValue,
    disabled: false,
    periodName: defaultPeriodName,
  };
};

/**
 * Generates all ASC period options for a selected fiscal year
 */
export const generateAscPeriodOptions = (
  selectedFiscalYear: FiscalYear,
): {
  defaultPeriodOption: PeriodNameOption | null;
  periodOptions: PeriodNameOptionGroup[];
} => {
  const { enabledDatetime, productType, taxYearEndDate } = selectedFiscalYear;

  if (!enabledDatetime || productType !== ProductType.UsAsc) {
    return {
      defaultPeriodOption: null,
      periodOptions: [],
    };
  }

  const yearEndMonthYear = dateToMonthYear(taxYearEndDate);
  const { month: endMonth, year: endYear } = yearEndMonthYear;

  const sortedQuarters = generateQuarterOptions(endMonth, endYear);
  const sortedMonths = generateMonthOptions(endMonth, endYear);
  const halves = generateHalfOptions(sortedQuarters);

  const defaultPeriodOption = generateDefaultAscPeriodOption(sortedMonths);

  if (!defaultPeriodOption) {
    return {
      defaultPeriodOption: null,
      periodOptions: [],
    };
  }

  const periodOptions: PeriodNameOptionGroup[] = [
    { label: 'Year', options: [defaultPeriodOption] },
    { label: 'Quarter', options: sortedQuarters },
    { label: 'Half', options: halves },
    { label: 'Month', options: sortedMonths },
  ];

  return {
    defaultPeriodOption,
    periodOptions,
  };
};

export type ProductPeriodSelection = {
  fiscalYearId: string;
  periodName: PeriodName;
  product: ProductType;
};

export const getProductAndPeriodFromLocalStorage = (companyId: string): ProductPeriodSelection | null => {
  const storedProduct = getProductFromLocalStorage(companyId);
  if (!storedProduct) {
    removeProductSelectionFromLocalStorage(companyId);
    return null;
  }
  const storedFiscalYearId = getFiscalYearIdFromLocalStorage(companyId, storedProduct);
  const storedPeriodName = getPeriodNameFromLocalStorage(companyId, storedProduct);
  if (!storedFiscalYearId || !storedPeriodName) {
    removeProductSelectionFromLocalStorage(companyId);
    return null;
  }

  return {
    fiscalYearId: storedFiscalYearId,
    periodName: storedPeriodName,
    product: storedProduct,
  };
};

const periodNameMapping: Record<CommonPeriodName, GqlPeriodName> = {
  [CommonPeriodName.A]: GqlPeriodName.A,
  [CommonPeriodName.Q1]: GqlPeriodName.Q1,
  [CommonPeriodName.Q2]: GqlPeriodName.Q2,
  [CommonPeriodName.Q3]: GqlPeriodName.Q3,
  [CommonPeriodName.Q4]: GqlPeriodName.Q4,
  [CommonPeriodName.H1]: GqlPeriodName.H1,
  [CommonPeriodName.H2]: GqlPeriodName.H2,
  [CommonPeriodName.M1]: GqlPeriodName.M1,
  [CommonPeriodName.M2]: GqlPeriodName.M2,
  [CommonPeriodName.M3]: GqlPeriodName.M3,
  [CommonPeriodName.M4]: GqlPeriodName.M4,
  [CommonPeriodName.M5]: GqlPeriodName.M5,
  [CommonPeriodName.M6]: GqlPeriodName.M6,
  [CommonPeriodName.M7]: GqlPeriodName.M7,
  [CommonPeriodName.M8]: GqlPeriodName.M8,
  [CommonPeriodName.M9]: GqlPeriodName.M9,
  [CommonPeriodName.M10]: GqlPeriodName.M10,
  [CommonPeriodName.M11]: GqlPeriodName.M11,
  [CommonPeriodName.M12]: GqlPeriodName.M12,
  [CommonPeriodName.YTD1]: GqlPeriodName.Ytd1,
  [CommonPeriodName.YTD2]: GqlPeriodName.Ytd2,
  [CommonPeriodName.YTD3]: GqlPeriodName.Ytd3,
  [CommonPeriodName.YTD4]: GqlPeriodName.Ytd4,
  [CommonPeriodName.YTD5]: GqlPeriodName.Ytd5,
  [CommonPeriodName.YTD6]: GqlPeriodName.Ytd6,
  [CommonPeriodName.YTD7]: GqlPeriodName.Ytd7,
  [CommonPeriodName.YTD8]: GqlPeriodName.Ytd8,
  [CommonPeriodName.YTD9]: GqlPeriodName.Ytd9,
  [CommonPeriodName.YTD10]: GqlPeriodName.Ytd10,
  [CommonPeriodName.YTD11]: GqlPeriodName.Ytd11,
  [CommonPeriodName.YTD12]: GqlPeriodName.Ytd12,
};

export const toGqlPeriodName = (periodName: CommonPeriodName): GqlPeriodName => periodNameMapping[periodName];

const YTD_PERIOD_NAMES = new Set<PeriodName>([
  GqlPeriodName.Ytd1,
  GqlPeriodName.Ytd2,
  GqlPeriodName.Ytd3,
  GqlPeriodName.Ytd4,
  GqlPeriodName.Ytd5,
  GqlPeriodName.Ytd6,
  GqlPeriodName.Ytd7,
  GqlPeriodName.Ytd8,
  GqlPeriodName.Ytd9,
  GqlPeriodName.Ytd10,
  GqlPeriodName.Ytd11,
  GqlPeriodName.Ytd12,
]);

/**
 * Checks if a period name is a YTD (year-to-date) period
 */
export const isYtdPeriodName = (periodName: PeriodName): boolean => YTD_PERIOD_NAMES.has(periodName);

/**
 * Returns the current valid YTD period for a fiscal year based on today's date.
 * Returns 'A' (Annual) if all 12 months are complete, or the appropriate YTD period.
 * Returns null if no months are complete yet.
 */
export const getCurrentValidYtdPeriodName = (fiscalYear: FiscalYear): PeriodName | null => {
  const { defaultPeriodOption } = generateAscPeriodOptions(fiscalYear);
  return defaultPeriodOption?.periodName ?? null;
};

/**
 * Extracts a sub-period from a given period based on the period name.
 * The fiscal year end month is inferred from the input period's end month.
 *
 * @param fiscalYearStartDate - The start date of the fiscal year
 * @param fiscalYearEndDate - The end date of the fiscal year
 * @param periodName - The period name to extract (e.g., Q1, M6, YTD3)
 * @returns A sub-period matching the periodName within the same fiscal year
 * @example
 * // For a fiscal year ending in December (Jan 2023 - Dec 2023)
 * getFiscalYearSubsetPeriod('2023-01-01', '2023-12-31', PeriodName.Q1) // Returns Jan 2023 - Mar 2023
 * getFiscalYearSubsetPeriod('2023-01-01', '2023-12-31', PeriodName.M6) // Returns Jun 2023 - Jun 2023
 * getFiscalYearSubsetPeriod('2023-01-01', '2023-12-31', PeriodName.YTD3) // Returns Jan 2023 - Mar 2023
 */
export const getFiscalYearSubsetPeriod = (
  fiscalYearStartDate: string,
  fiscalYearEndDate: string,
  periodName: PeriodName,
): MonthYearPeriod => {
  const fiscalYearPeriod = toMonthYearPeriod(new Date(fiscalYearStartDate), new Date(fiscalYearEndDate));
  const fiscalYearStartMonth = fiscalYearPeriod.startMonthYear.month;
  const fiscalYearStartYear = fiscalYearPeriod.startMonthYear.year;

  // Helper to convert fiscal month index (0-11) to calendar month
  const fiscalMonthIndexToCalendarMonth = (fiscalMonthIndex: number): MonthYear => {
    const calendarMonth = (fiscalYearStartMonth + fiscalMonthIndex) % 12;
    // Determine the year: if we've wrapped around past December, increment year
    const yearOffset = fiscalYearStartMonth + fiscalMonthIndex >= 12 ? 1 : 0;
    const year = fiscalYearStartYear + yearOffset;
    return { month: calendarMonth, year };
  };

  switch (periodName) {
    // Annual - return full period
    case PeriodName.A:
      return fiscalYearPeriod;

    // Quarters
    case PeriodName.Q1: {
      const startMonthYear = fiscalMonthIndexToCalendarMonth(0);
      const endMonthYear = fiscalMonthIndexToCalendarMonth(2);
      return { startMonthYear, endMonthYear };
    }
    case PeriodName.Q2: {
      const startMonthYear = fiscalMonthIndexToCalendarMonth(3);
      const endMonthYear = fiscalMonthIndexToCalendarMonth(5);
      return { startMonthYear, endMonthYear };
    }
    case PeriodName.Q3: {
      const startMonthYear = fiscalMonthIndexToCalendarMonth(6);
      const endMonthYear = fiscalMonthIndexToCalendarMonth(8);
      return { startMonthYear, endMonthYear };
    }
    case PeriodName.Q4: {
      const startMonthYear = fiscalMonthIndexToCalendarMonth(9);
      const endMonthYear = fiscalMonthIndexToCalendarMonth(11);
      return { startMonthYear, endMonthYear };
    }

    // Half years
    case PeriodName.H1: {
      const startMonthYear = fiscalMonthIndexToCalendarMonth(0);
      const endMonthYear = fiscalMonthIndexToCalendarMonth(5);
      return { startMonthYear, endMonthYear };
    }
    case PeriodName.H2: {
      const startMonthYear = fiscalMonthIndexToCalendarMonth(6);
      const endMonthYear = fiscalMonthIndexToCalendarMonth(11);
      return { startMonthYear, endMonthYear };
    }

    // Months (M1-M12)
    case PeriodName.M1: {
      const monthYear = fiscalMonthIndexToCalendarMonth(0);
      return { startMonthYear: monthYear, endMonthYear: monthYear };
    }
    case PeriodName.M2: {
      const monthYear = fiscalMonthIndexToCalendarMonth(1);
      return { startMonthYear: monthYear, endMonthYear: monthYear };
    }
    case PeriodName.M3: {
      const monthYear = fiscalMonthIndexToCalendarMonth(2);
      return { startMonthYear: monthYear, endMonthYear: monthYear };
    }
    case PeriodName.M4: {
      const monthYear = fiscalMonthIndexToCalendarMonth(3);
      return { startMonthYear: monthYear, endMonthYear: monthYear };
    }
    case PeriodName.M5: {
      const monthYear = fiscalMonthIndexToCalendarMonth(4);
      return { startMonthYear: monthYear, endMonthYear: monthYear };
    }
    case PeriodName.M6: {
      const monthYear = fiscalMonthIndexToCalendarMonth(5);
      return { startMonthYear: monthYear, endMonthYear: monthYear };
    }
    case PeriodName.M7: {
      const monthYear = fiscalMonthIndexToCalendarMonth(6);
      return { startMonthYear: monthYear, endMonthYear: monthYear };
    }
    case PeriodName.M8: {
      const monthYear = fiscalMonthIndexToCalendarMonth(7);
      return { startMonthYear: monthYear, endMonthYear: monthYear };
    }
    case PeriodName.M9: {
      const monthYear = fiscalMonthIndexToCalendarMonth(8);
      return { startMonthYear: monthYear, endMonthYear: monthYear };
    }
    case PeriodName.M10: {
      const monthYear = fiscalMonthIndexToCalendarMonth(9);
      return { startMonthYear: monthYear, endMonthYear: monthYear };
    }
    case PeriodName.M11: {
      const monthYear = fiscalMonthIndexToCalendarMonth(10);
      return { startMonthYear: monthYear, endMonthYear: monthYear };
    }
    case PeriodName.M12: {
      const monthYear = fiscalMonthIndexToCalendarMonth(11);
      return { startMonthYear: monthYear, endMonthYear: monthYear };
    }

    // YTD periods (YTD1-YTD12)
    case PeriodName.Ytd1: {
      const startMonthYear = fiscalMonthIndexToCalendarMonth(0);
      const endMonthYear = fiscalMonthIndexToCalendarMonth(0);
      return { startMonthYear, endMonthYear };
    }
    case PeriodName.Ytd2: {
      const startMonthYear = fiscalMonthIndexToCalendarMonth(0);
      const endMonthYear = fiscalMonthIndexToCalendarMonth(1);
      return { startMonthYear, endMonthYear };
    }
    case PeriodName.Ytd3: {
      const startMonthYear = fiscalMonthIndexToCalendarMonth(0);
      const endMonthYear = fiscalMonthIndexToCalendarMonth(2);
      return { startMonthYear, endMonthYear };
    }
    case PeriodName.Ytd4: {
      const startMonthYear = fiscalMonthIndexToCalendarMonth(0);
      const endMonthYear = fiscalMonthIndexToCalendarMonth(3);
      return { startMonthYear, endMonthYear };
    }
    case PeriodName.Ytd5: {
      const startMonthYear = fiscalMonthIndexToCalendarMonth(0);
      const endMonthYear = fiscalMonthIndexToCalendarMonth(4);
      return { startMonthYear, endMonthYear };
    }
    case PeriodName.Ytd6: {
      const startMonthYear = fiscalMonthIndexToCalendarMonth(0);
      const endMonthYear = fiscalMonthIndexToCalendarMonth(5);
      return { startMonthYear, endMonthYear };
    }
    case PeriodName.Ytd7: {
      const startMonthYear = fiscalMonthIndexToCalendarMonth(0);
      const endMonthYear = fiscalMonthIndexToCalendarMonth(6);
      return { startMonthYear, endMonthYear };
    }
    case PeriodName.Ytd8: {
      const startMonthYear = fiscalMonthIndexToCalendarMonth(0);
      const endMonthYear = fiscalMonthIndexToCalendarMonth(7);
      return { startMonthYear, endMonthYear };
    }
    case PeriodName.Ytd9: {
      const startMonthYear = fiscalMonthIndexToCalendarMonth(0);
      const endMonthYear = fiscalMonthIndexToCalendarMonth(8);
      return { startMonthYear, endMonthYear };
    }
    case PeriodName.Ytd10: {
      const startMonthYear = fiscalMonthIndexToCalendarMonth(0);
      const endMonthYear = fiscalMonthIndexToCalendarMonth(9);
      return { startMonthYear, endMonthYear };
    }
    case PeriodName.Ytd11: {
      const startMonthYear = fiscalMonthIndexToCalendarMonth(0);
      const endMonthYear = fiscalMonthIndexToCalendarMonth(10);
      return { startMonthYear, endMonthYear };
    }
    case PeriodName.Ytd12: {
      const startMonthYear = fiscalMonthIndexToCalendarMonth(0);
      const endMonthYear = fiscalMonthIndexToCalendarMonth(11);
      return { startMonthYear, endMonthYear };
    }

    default:
      throw createError('InvalidPeriodError', `Unknown period name: ${periodName}`);
  }
};
